import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  ComponentRef,
  createComponent,
  ElementRef,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, Observable, throwError } from 'rxjs';
import { catchError, delay, mergeMap, take } from 'rxjs/operators';
import {
  ActiveNotification,
  isNullish,
  NotificationService,
  WINDOW,
} from 'wflow-cdk';
import { OfflineOverlayComponent } from '../components/offline-overlay/offline-overlay.component';

@Injectable()
export class NetworkRetryInterceptor implements HttpInterceptor {
  readonly injector = inject(Injector);
  readonly notification = inject(NotificationService);
  readonly translate = inject(TranslateService);
  readonly wind = inject(WINDOW);

  constructor() {
    const root = this.wind.document.body;
    let activeNotification: ActiveNotification<unknown>;
    let overlayRef: ComponentRef<unknown>;

    // Listen to online/offline events
    fromEvent(this.wind, 'offline').subscribe(() => {
      console.log('Connection lost. Waiting for reconnection.');
      overlayRef = this.createOverlay(root);
      activeNotification = this.notification.error(
        {
          text: this.translate.instant('OFFLINE_MESSAGE.Description'),
          title: this.translate.instant('OFFLINE_MESSAGE.Title'),
        },
        {
          disableTimeOut: true,
          closeButton: false,
          onActivateTick: false,
          tapToDismiss: false,
        },
      );
    });

    fromEvent(this.wind, 'online')
      .pipe(delay(500))
      .subscribe(() => {
        console.log('Connection restored. Retry requests if needed.');
        this.removeOverlay(overlayRef);
        activeNotification?.toastRef?.close();

        this.notification.info({
          text: this.translate.instant('ONLINE_MESSAGE.Description'),
          title: this.translate.instant('ONLINE_MESSAGE.Title'),
        });
      });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // First, manually check if the user is offline
    if (!navigator.onLine) {
      console.error('No internet connection detected.', req.url);
      return this.retryOnReconnect(req, next); // Retry when back online
    }

    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!this.wind.navigator.onLine) {
          console.error('No Internet Connection.');
          return this.retryOnReconnect(req, next);
        }

        return throwError(err);
      }),
    );
  }

  private retryOnReconnect(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return fromEvent(window, 'online').pipe(
      take(1),
      delay(1000), // Add a delay to allow connection stabilization
      mergeMap(() => {
        console.log('Retrying request:', req.url);
        return next.handle(req);
      }),
    );
  }

  private createOverlay(parentElement: HTMLElement): ComponentRef<unknown> {
    const el = this.wind.document.createElement('DIV');
    el.classList.add('offline-overlay');
    parentElement.appendChild(el);

    const component = createComponent(OfflineOverlayComponent, {
      environmentInjector: this.injector.get(EnvironmentInjector),
      hostElement: el,
    });

    component.changeDetectorRef.detectChanges();
    return component;
  }

  private removeOverlay(component: ComponentRef<unknown>) {
    if (isNullish(component)) {
      return;
    }
    const el = component.injector.get(ElementRef<HTMLElement>)?.nativeElement;
    if (el?.parentElement) {
      el.parentElement.removeChild(el);
    }
  }
}
